import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { AppContext } from '../../App';
import { useIsLabViewer } from '../../hooks/useIsLabViewer';
import { getProjects } from '../../clients/project_client';
import InfiniteScroll from 'react-infinite-scroller';
import ProjectTable from '../../components/Project/ProjectTable';
import { Spinner } from 'react-bootstrap';
import { showToast } from '../../config_and_helpers/helpers';
import GenericSearchFilter from '../../components/Generic/GenericSearchFilter';
import ProjectFormModal from '../../components/Project/ProjectFormModal';
import ProjectFilterComponent from '../../components/Project/ProjectFilterComponent';
import {
  logError,
  logInfo,
  logListSearchEvent,
  logListSearchError,
} from '../../config_and_helpers/logger';
import {
  abortListSearchController,
  getNextPageUrlFilterMismatch,
  isPartialListSearchQuery,
  isRunnableListSearchQuery,
  resetPaginationForPartialSearch,
  shouldBootstrapInfiniteScrollLoad,
  INFINITE_SCROLL_THRESHOLD_PX,
} from '../../utils/listSearchRequest';
import GlassWorkspaceShell from '../../components/Glass/GlassWorkspaceShell';
import { PROJECT_STATUS_OPTIONS } from '../../config_and_helpers/config';

/**
 * ProjectsPage is a page component responsible for rendering a searchable list of projects.
 * It supports:
 * - Debounced text-based search
 * - Infinite scroll for paginated data loading
 * - Modal for creating new projects
 *
 * @returns {JSX.Element} - The rendered projects page.
 */
const ProjectsPage = () => {
  const { token } = useContext(AppContext);
  const isViewer = useIsLabViewer();

  // Raw and filtered projects
  const [baseProjects, setBaseProjects] = useState([]);
  const [viewProjects, setViewProjects] = useState([]);

  // Filters and UI state
  const [nextPageUrl, setNextPageUrl] = useState(null);
  const [hasMore, setHasMore] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
  });
  const [showFilters, setShowFilters] = useState(false);
  const [sortBy, setSortBy] = useState('updated_at');
  const [sortDirection, setSortDirection] = useState('desc');

  const isMountedRef = useRef(false);
  const debounceTimeoutRef = useRef(null);
  const currentRequestRef = useRef(null);
  const hasMoreRef = useRef(true);

  // Track component mount state to prevent updates after unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Fetch projects with optional search and pagination.
   */
  const fetchProjects = useCallback(
    (
      {
        searchValue = '',
        nextPage = null,
        ownerFilter = '',
        statusFilter = '',
        sortByValue = 'updated_at',
        sortDirectionValue = 'desc',
      } = {},
      newSearch = false
    ) => {
      const trigger = newSearch ? 'search-or-filter' : 'scroll-or-refresh';
      const params = {
        searchInput: searchValue,
        nextPage,
        ownerFilter,
        statusFilter,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
      };
      // Prevent fetching if we know there are no more pages (unless it's a new search)
      if (!newSearch && !hasMoreRef.current && nextPage) {
        return;
      }

      // Prevent multiple simultaneous requests
      if (!newSearch && isLoading) {
        return;
      }

      // Additional safety check
      if (!hasMoreRef.current && nextPage) {
        return;
      }

      // Supersede in-flight request only when starting a replacement fetch
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }

      // Set loading state
      setIsLoading(true);
      if (newSearch) {
        setIsSearching(true);
        setBaseProjects([]);
        setViewProjects([]);
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Create a cancelable promise
      const controller = new AbortController();
      currentRequestRef.current = controller;

      getProjects(token, setBaseProjects, {
        searchInput: searchValue,
        nextPage,
        ownerFilter,
        statusFilter,
        sortBy: sortByValue,
        sortDirection: sortDirectionValue,
        signal: controller.signal,
      })
        .then(response => {
          if (!isMountedRef.current) return;
          if (currentRequestRef.current !== controller) {
            return;
          }

          if (response?.canceled) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          const responseMeta = {
            success: response && response.success,
            reachedEnd: !!response?.reachedEnd,
            nextPage: response?.nextPage,
          };

          if (response && response.success) {
            // Clean the nextPage URL to prevent parameter accumulation
            let cleanNextPage = response.nextPage;
            if (cleanNextPage && !response.reachedEnd) {
              try {
                const url = new URL(cleanNextPage);
                const cleanUrl = new URL(url.origin + url.pathname);
                cleanUrl.searchParams.set(
                  'page_num',
                  url.searchParams.get('page_num')
                );
                if (searchValue) {
                  cleanUrl.searchParams.set('search', searchValue);
                }
                if (statusFilter) {
                  cleanUrl.searchParams.set('status', statusFilter);
                }
                if (sortByValue) {
                  cleanUrl.searchParams.set('sort_by', sortByValue);
                }
                if (sortDirectionValue) {
                  cleanUrl.searchParams.set('sort_direction', sortDirectionValue);
                }
                cleanNextPage = cleanUrl.toString();
              } catch (error) {
                logError(
                  'ProjectsPage',
                  'Error cleaning nextPage URL',
                  error
                );
              }
            }

            setNextPageUrl(response.reachedEnd ? null : cleanNextPage);
            setHasMore(!response.reachedEnd);
            hasMoreRef.current = !response.reachedEnd;
          } else {
            setHasMore(false);
            hasMoreRef.current = false;
            setNextPageUrl(null);

            showToast(
              'An unexpected error occurred. Please try again',
              'error',
              'top-center',
              3000
            );
          }

          logListSearchEvent(
            'projects',
            response && response.success ? 'success' : 'failure',
            {
              trigger,
              params,
              responseMeta,
            }
          );

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        })
        .catch(error => {
          if (currentRequestRef.current !== controller) {
            return;
          }
          if (
            error?.name === 'AbortError' ||
            error?.code === 'ERR_CANCELED'
          ) {
            setIsLoading(false);
            setIsSearching(false);
            currentRequestRef.current = null;
            return;
          }

          if (!isMountedRef.current) return;

          logListSearchError('projects', trigger, error, { params });

          setHasMore(false);
          hasMoreRef.current = false;
          setNextPageUrl(null);

          setIsLoading(false);
          setIsSearching(false);
          currentRequestRef.current = null;
        });
    },
    [token, isLoading]
  );

  // Cleanup effect for debounce timeout and requests
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (currentRequestRef.current) {
        abortListSearchController(currentRequestRef);
      }
    };
  }, []);

  /**
   * Smart debounced search input handler with immediate UI feedback
   */
  const debouncedFetch = useCallback(
    value => {
      // Clear previous timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      // Immediate UI feedback
      if (value.length >= 2) {
        setBaseProjects([]);
        setViewProjects([]);
        setIsSearching(true);
        setHasMore(true);
        hasMoreRef.current = true;
        setNextPageUrl(null);
      }

      // Only search if we have 2+ characters or empty string
      if (isRunnableListSearchQuery(value)) {
        debounceTimeoutRef.current = setTimeout(() => {
      fetchProjects(
        {
          searchValue: value,
          nextPage: null,
          ownerFilter: '',
          statusFilter: filters.status,
          sortByValue: sortBy,
          sortDirectionValue: sortDirection,
        },
        true
      );
        }, 300);
      } else if (isPartialListSearchQuery(value)) {
        setBaseProjects([]);
        setViewProjects([]);
        setIsSearching(false);
        resetPaginationForPartialSearch({
          setHasMore,
          hasMoreRef,
          setNextPageUrl,
          abortControllerRef: currentRequestRef,
        });
      }
    },
    [fetchProjects, filters.status, sortBy, sortDirection]
  );

  /**
   * Handle filter changes
   */
  const handleApplyFilters = newFilters => {
    fetchProjects(
      {
        searchValue: searchInput,
        nextPage: null,
        ownerFilter: '',
        statusFilter: newFilters.status || '',
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    );
  };

  /**
   * Handle clearing all filters
   */
  const handleClearFilters = () => {
    fetchProjects(
      {
        searchValue: searchInput,
        nextPage: null,
        ownerFilter: '',
        statusFilter: '',
        sortByValue: sortBy,
        sortDirectionValue: sortDirection,
      },
      true
    );
  };

  /**
   * Toggle filter panel visibility
   */
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const handleSortByChange = (value, direction = 'asc') => {
    setSortBy(value);
    setSortDirection(direction);
    fetchProjects(
      {
        searchValue: searchInput,
        nextPage: null,
        ownerFilter: '',
        statusFilter: filters.status,
        sortByValue: value,
        sortDirectionValue: direction,
      },
      true
    );
  };


  /**
   * Handles updates to the search input.
   */
  const handleSearchInput = value => {
    setSearchInput(value);
    debouncedFetch(value);
  };

  /**
   * Clears the current search value.
   */
  const resetSearchValue = () => setSearchInput('');

  useEffect(() => {
    if (!baseProjects?.length) return;
    logInfo('ProjectsPage', 'project list owner debug', {
      count: baseProjects.length,
      sample: baseProjects.slice(0, 10).map(p => ({
        id: p?.id,
        name: p?.name,
        owner: p?.owner,
        owner_id: p?.owner_id,
        owner_name: p?.owner_name,
        owner_email: p?.owner_email,
        owner_username: p?.owner_username,
      })),
    });
  }, [baseProjects]);

  const projectsEmptyMessage = (() => {
    if (searchInput.length >= 2) {
      return `No projects match your search for "${searchInput}"`;
    }
    if (filters.status) {
      const statusOption = PROJECT_STATUS_OPTIONS.find(
        option => option.value === filters.status
      );
      const label = statusOption?.label || filters.status;
      return `No ${label.toLowerCase()} projects available`;
    }
    return 'No projects available';
  })();

  return (
    <GlassWorkspaceShell theme="yellow" wide>
      <div className="glass-toolbar">
        <GenericSearchFilter
          entityType="projects"
          placeHolderLabel="Search projects..."
          searchInput={searchInput}
          onSearchInput={handleSearchInput}
          entity=""
          onEntityChange={() => {}}
          hideEntityDropdown
          showFilters={showFilters}
          toggleFilters={toggleFilters}
          ModalComponent={
            isViewer ? null : (
            <ProjectFormModal
              onSuccessfulSubmit={() =>
                fetchProjects(
                  {
                    searchValue: searchInput,
                    nextPage: null,
                    ownerFilter: '',
                    statusFilter: filters.status,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  true
                )
              }
              clearSearchValue={resetSearchValue}
            />
            )
          }
        />
        {showFilters ? (
          <ProjectFilterComponent
            filters={filters}
            setFilters={setFilters}
            onApplyFilters={handleApplyFilters}
            onClearFilters={handleClearFilters}
            embeddedInToolbar
          />
        ) : null}
      </div>

      {(isLoading && !baseProjects.length && !viewProjects.length) ||
      isSearching ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" />
          {isSearching && searchInput.length >= 2 && (
            <div className="mt-2 text-muted">Searching projects...</div>
          )}
        </div>
      ) : (
            <InfiniteScroll
              key={`${searchInput}-${filters.status}-${sortBy}-${sortDirection}-${hasMore}`}
              pageStart={0}
              initialLoad={hasMoreRef.current}
              loadMore={() => {
                if (
                  isLoading ||
                  !hasMoreRef.current ||
                  isPartialListSearchQuery(searchInput)
                ) {
                  return;
                }

                const itemCount =
                  (viewProjects.length || baseProjects.length) || 0;

                // InfiniteScroll initialLoad: fetch page 1 when the list is still empty.
                if (
                  shouldBootstrapInfiniteScrollLoad({
                    nextPageUrl,
                    itemCount,
                  })
                ) {
                  fetchProjects(
                    {
                      searchValue: searchInput,
                      nextPage: null,
                      ownerFilter: '',
                      statusFilter: filters.status,
                      sortByValue: sortBy,
                      sortDirectionValue: sortDirection,
                    },
                    true
                  );
                  return;
                }

                // Pagination requires a real next-page URL.
                if (!nextPageUrl) {
                  return;
                }

                const mismatch = getNextPageUrlFilterMismatch(nextPageUrl, {
                  search: searchInput,
                  status: filters.status || '',
                  sort_by: sortBy,
                  sort_direction: sortDirection,
                });
                if (mismatch === null) {
                  logListSearchError(
                    'projects',
                    'next-page-url-stale-check',
                    new Error('Failed to parse nextPageUrl'),
                    { nextPageUrl, searchInput, status: filters.status, sortBy, sortDirection }
                  );
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }
                if (mismatch.mismatched) {
                  logListSearchEvent('projects', 'next-page-url-stale-mismatch', {
                    nextPageUrl,
                    expected: mismatch.expected,
                    actual: mismatch.actual,
                  });
                  setNextPageUrl(null);
                  setHasMore(false);
                  hasMoreRef.current = false;
                  return;
                }

                fetchProjects(
                  {
                    searchValue: searchInput,
                    nextPage: nextPageUrl,
                    ownerFilter: '',
                    statusFilter: filters.status,
                    sortByValue: sortBy,
                    sortDirectionValue: sortDirection,
                  },
                  false
                );
              }}
              hasMore={hasMore && !isLoading}
              loader={
                <div key="projects-loader" className="text-center py-2">
                  <Spinner animation="border" size="sm" role="status" />
                  <div className="mt-1">
                    <small className="text-muted">
                      Loading more projects...
                    </small>
                  </div>
                </div>
              }
              useWindow={true}
              threshold={INFINITE_SCROLL_THRESHOLD_PX}
            >
              {!(viewProjects.length || baseProjects.length) ? (
                <div className="glass-empty">
                  <div
                    className="mb-3"
                    style={{ fontSize: '2.5rem' }}
                    aria-hidden="true"
                  >
                    📁
                  </div>
                  <h5 className="mb-2">No projects found</h5>
                  <p className="mb-3">{projectsEmptyMessage}</p>
                  {searchInput.length >= 2 && (
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-sm"
                      onClick={() => {
                        setSearchInput('');
                        fetchProjects(
                          {
                            searchValue: '',
                            nextPage: null,
                            ownerFilter: '',
                            statusFilter: filters.status,
                            sortByValue: sortBy,
                            sortDirectionValue: sortDirection,
                          },
                          true
                        );
                      }}
                    >
                      Clear search
                    </button>
                  )}
                </div>
              ) : (
                <div key="project-table" className="glass-table-wrap">
                  <ProjectTable
                    projectList={
                      viewProjects.length ? viewProjects : baseProjects
                    }
                    sortBy={sortBy}
                    sortDirection={sortDirection}
                    onSort={handleSortByChange}
                    handleDelete={() =>
                      fetchProjects(
                        {
                          searchValue: searchInput,
                          nextPage: null,
                          ownerFilter: '',
                          statusFilter: filters.status,
                          sortByValue: sortBy,
                          sortDirectionValue: sortDirection,
                        },
                        true
                      )
                    }
                    clearSearchValue={resetSearchValue}
                  />
                </div>
              )}
            </InfiniteScroll>
      )}
    </GlassWorkspaceShell>
  );
};

export default ProjectsPage;


